import { useState } from 'react';
import styles from './useTableColumns.module.scss';
import { Flex } from 'antd';
import { Button, Sidebar, Tooltip } from 'common';
import toast from 'react-hot-toast';

const CopyTextButton = ({ value = '' }) => {
  const [isCopied, setIsCopied] = useState(false);

  const copyPopoverText = async () => {
    try {
      await navigator.clipboard.writeText(typeof value === 'string' ? value : String(value ?? ''));
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const copyField = () => {
    setTimeout(() => {
      copyPopoverText();
      setIsCopied(true);
    }, 200);

    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  return (
    <Button
      type="link"
      size="small"
      className={[styles.popoverLink]}
      icon={isCopied ? 'IoCheckmarkCircle' : 'CopyBtnIcon'}
      iconSize="18px"
      text="Copy Text"
      onClick={(e) => {
        e.stopPropagation();
        copyField();
      }}
    />
  );
};

const RenderPopoverFooter = ({ show = true, onViewMore = () => {}, value = '' }) => {
  if (!show) return null;
  return (
    <Flex align="center" justify="space-between" className={styles.popoverFooter} data-tag="allowRowEvents">
      <Button
        type="link"
        size="small"
        className={[styles.popoverLink, styles.popoverLinkSecondary]}
        icon="ViewIcon"
        iconSize="18px"
        text="View More"
        iconColor="var(--ant-color-secondary)"
        onClick={(e) => {
          e.stopPropagation();
          onViewMore();
        }}
      />
      <CopyTextButton value={value} />
    </Flex>
  );
};

const RenderHoverCopyText = ({ text, limit = 60, heading = 'Details' }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const safeText =
    typeof text === 'string'
      ? text
      : typeof text === 'object' && text !== null
        ? JSON.stringify(text, null, 2)
        : String(text ?? '');

  const isHtml = /<[a-z!/][\s\S]*?>/i.test(safeText);

  let isJson = false;
  let formattedJson = safeText;

  if (!isHtml) {
    try {
      const parsed = JSON.parse(safeText);
      isJson = typeof parsed === 'object' && parsed !== null;
      formattedJson = JSON.stringify(parsed, null, 2);
    } catch {
      // Not JSON
    }
  }

  const plainText = isHtml
    ? safeText
        .replace(/<(script|style)[\s\S]*?<\/\1>/gi, ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
    : isJson
      ? formattedJson
      : safeText;

  const normalized = plainText.replace(/\s+/g, ' ').trim();
  const displayText = normalized.length > limit ? `${normalized.slice(0, limit)}...` : normalized;

  const showTooltip = isHtml || isJson || normalized.length > limit;
  const footerEnabled = true;

  const copyValue = isJson ? formattedJson : safeText;

  const fullContent = isHtml ? (
    <div className={styles.htmlPreview} dangerouslySetInnerHTML={{ __html: safeText }} />
  ) : isJson ? (
    <pre className={styles.jsonPreview}>{formattedJson}</pre>
  ) : (
    <pre className={styles.textPreview}>{safeText}</pre>
  );

  const tooltipTitle = (
    <div className={styles.popoverBody}>
      <div className={styles.popoverContent}>{fullContent}</div>
      <RenderPopoverFooter
        show={footerEnabled}
        onViewMore={() => {
          setTooltipOpen(false);
          setDrawerOpen(true);
        }}
        value={copyValue}
      />
    </div>
  );

  const drawerHeading = (
    <Flex align="center" gap={12} className={styles.drawerHeading} data-tag="allowRowEvents">
      <span className={'h4-medium'}>{heading}</span>
      <CopyTextButton value={copyValue} />
    </Flex>
  );

  const drawerContent = (
    <div className={styles.drawerContent} data-tag="allowRowEvents">
      {fullContent}
    </div>
  );

  return (
    <div className="body-12-medium" data-tag="allowRowEvents" style={{ whiteSpace: 'pre-wrap' }}>
      {showTooltip ? (
        <Tooltip
          title={tooltipTitle}
          trigger="hover"
          open={tooltipOpen}
          onOpenChange={setTooltipOpen}
          color="var(--user-dropdown-bg)"
          overlayClassName={styles.tableTooltip}
          overlayInnerStyle={{
            color: 'var(--text-main)',
            backgroundColor: 'var(--user-dropdown-bg)',
            whiteSpace: 'pre-wrap',
            border: '1px solid var(--user-card-border)',
            borderRadius: 12,
            padding: '12px 14px',
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
          }}
        >
          <span className={styles.triggerText}>{displayText}</span>
        </Tooltip>
      ) : (
        <span className={styles.triggerText}>{safeText}</span>
      )}

      {footerEnabled && (
        <Sidebar
          sidebarOpen={drawerOpen}
          setSidebarOpen={setDrawerOpen}
          title
          heading={drawerHeading}
          sidebarWidth={520}
          content={drawerContent}
        />
      )}
    </div>
  );
};

export default RenderHoverCopyText;
